import {
  BuyOrder,
  IncomingProduct,
  ProductProps,
  ProductPurchaseDefaults,
  ProductUnit,
  ProfitPushStatus,
} from "api/types";
import {
  useDisclosure,
  Collapse,
  Portal,
  CloseButton,
  Button,
  Text,
  Icon,
  VStack,
  Box,
  Flex,
  FormLabel,
  InputGroup,
  Input,
  SlideFade,
  useColorModeValue,
  useToast,
  Table,
  Tbody,
  Tfoot,
  Td,
  Th,
  Thead,
  Tr,
  Tooltip,
  Checkbox,
  Switch,
} from "@chakra-ui/react";

import { MdAddCircle } from "react-icons/md";
import { FaShip } from "react-icons/fa6";
import {
  MdModeEdit,
  MdSave,
  MdDownload,
  MdCloudUpload,
  MdOutlineMailOutline,
} from "react-icons/md";
import { IoIosRemoveCircle } from "react-icons/io";
import { TbBrandMonday, TbBuildingWarehouse } from "react-icons/tb";

import { CustomAlert } from "components/alerts/CustomAlert";
import { Select, OptionBase, GroupBase } from "chakra-react-select";

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ChangeEvent,
} from "react";
import { ApiUpdateOrderList } from "api/coverage/buyOrderList";
import { apiGetSimulationDetail } from "api/simulation/loadSavedSimulation";
import type { SimulatedPurchaseOrderRef } from "api/simulation/simulationsList";
import { apiCreateSupplyDocumentsForOrders } from "api/supplyDocuments/createSupplyDocument";
import {
  buyOrderDeleteBlockedMessage,
  isPersistedSupplyDocumentOrder,
  ordersPendingSupplyDocumentCreate,
  REAL_PURCHASE_ORDER_MESSAGE,
} from "api/supplyDocuments/buyOrderSupplyDocumentUtils";
import { apiDeleteSupplyDocument } from "api/supplyDocuments/deleteSupplyDocument";
import { apiUpdateSupplyDocumentLines } from "api/supplyDocuments/updateSupplyDocumentLines";
import { apiDownloadPurchaseOrderPdf } from "api/supplyDocuments/downloadPurchaseOrderPdf";
import { apiSavePurchaseOrderAudit } from "api/supplyDocuments/saveAudit";
import {
  apiSendPurchaseOrderEmail,
  type SendPurchaseOrderEmailErrorBody,
} from "api/supplyDocuments/sendPurchaseOrderEmail";
import { DriveFolderPickerModal } from "./DriveFolderPickerModal";
import { apiListSupplyDocumentsForSimulation } from "api/supplyDocuments/listSupplyDocuments";
import {
  mapSupplyDocumentsToBuyOrders,
  mergeSessionOrdersWithSupplyDocuments,
} from "api/supplyDocuments/mapSupplyDocumentsToBuyOrders";
import { apiProcurementExportSupplyDocument } from "api/supplyDocuments/procurementExport";
import {
  apiPushOrderToProfit,
  ProfitExportError,
} from "api/purchasing/profitExport";
import { usePurchaseDefaults } from "../hooks/usePurchaseDefaults";
import { useProfitPushStatus } from "../hooks/useProfitPushStatus";
import { canPushToProfit } from "../types/profitPushState";
import { checkProfitReadiness } from "../types/profitOrderReadiness";
import ProfitStatusBadge from "./ProfitStatusBadge";
import ProfitPushConfirmModal from "./ProfitPushConfirmModal";

function productErpCode(product: ProductProps): string {
  return String(product.erp_code ?? product.erpCode ?? "").trim();
}

function findProductByCode(
  catalog: ProductProps[],
  productCode: string,
): ProductProps | undefined {
  const code = productCode.trim();
  return catalog.find((item) => productErpCode(item) === code);
}

function purchaseOrderRefsToBuyOrders(
  refs: SimulatedPurchaseOrderRef[],
): BuyOrder[] {
  return refs.map((ref) => ({
    id: ref.id,
    number: ref.document_number || ref.id,
    ata: new Date(),
    ataw: new Date(),
    incoming_product_list: [],
  }));
}

const FOB_UNIT_ATTR = "Costo FOB x Unidad";

function parseNumericAttribute(value: string | number | undefined): number {
  if (value == null || value === "") {
    return 0;
  }
  const token = String(value).trim().split(/\s+/)[0]?.replace(",", ".");
  const parsed = Number(token);
  return Number.isFinite(parsed) ? parsed : 0;
}

function fobPerUnitFromProduct(attrs: ProductProps | undefined): number {
  const attr = attrs?.attributes?.find((item) => item.name === FOB_UNIT_ATTR);
  return parseNumericAttribute(attr?.value);
}

function parseBulkMetric(value: string | number | null | undefined): number {
  if (value == null || value === "") {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function bulkCbmPerUnit(pkg: ProductUnit | undefined): number {
  if (!pkg) {
    return 0;
  }
  const cubicMeasure = parseBulkMetric(pkg.cubic_measure);
  return cubicMeasure > 0 ? cubicMeasure / 1_000_000 : 0;
}

function bulkWeightPerUnit(pkg: ProductUnit | undefined): number {
  return parseBulkMetric(pkg?.weigth);
}

function lineWeightKg(unitWeight: number, bulks: number): number {
  return parseBulkMetric(unitWeight) * parseBulkMetric(bulks);
}

function formatMoney(value: number): string {
  return value.toLocaleString("defaults", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function toDateInputValue(value: Date | string | undefined): string {
  if (value == null) {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const PRODUCT_SELECT_COLUMN_WIDTH = "165px";
const ORDER_LINES_TABLE_MIN_WIDTH = "837px";

interface ProductOption extends OptionBase {
  label: string;
  value: string;
  description: string;
}

function formatProductOptionLabel(
  option: ProductOption,
  meta: { context: "menu" | "value" },
) {
  const isValue = meta.context === "value";
  return (
    <Box py={isValue ? 0 : 0.5} w="100%">
      <Text
        fontWeight="700"
        fontSize="xs"
        lineHeight="short"
        noOfLines={1}
        title={option.value}
      >
        {option.value}
      </Text>
      {option.description ? (
        <Text
          fontSize={isValue ? "10px" : "xs"}
          lineHeight="short"
          whiteSpace="normal"
          wordBreak="break-word"
          color={isValue ? "gray.400" : undefined}
        >
          {option.description}
        </Text>
      ) : null}
    </Box>
  );
}

const productSelectMenuPortalTarget =
  typeof document !== "undefined" ? document.body : undefined;

const productSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 1600,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    minWidth: "max(100%, 360px)",
    width: "max-content",
    maxWidth: "min(90vw, 520px)",
  }),
};

const productSelectChakraStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    width: "100%",
    minWidth: 0,
  }),
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    minHeight: "28px",
    height: "auto",
    alignItems: "flex-start",
    width: "100%",
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    py: "2px",
    px: "4px",
    flexWrap: "wrap" as const,
    width: "100%",
    alignItems: "flex-start",
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    position: "relative" as const,
    top: "auto",
    transform: "none",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    lineHeight: "1.2",
    maxWidth: "100%",
    width: "100%",
    margin: 0,
  }),
  option: (provided: Record<string, unknown>) => ({
    ...provided,
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
  }),
};

const IncomingProductRow = (props: {
  id: number;
  product: IncomingProduct;
  selection: ProductProps[];
  removeMethod: (id: number) => void;
  updateMethod: (id: number, newProduct: IncomingProduct) => void;
  showPurchaseData?: boolean;
  purchaseDefaults?: ProductPurchaseDefaults;
}) => {
  const {
    id,
    product,
    selection,
    removeMethod,
    updateMethod,
    showPurchaseData = false,
    purchaseDefaults,
  } = props;

  const options: ProductOption[] = selection.map((_product) => {
    const code = productErpCode(_product);
    const description = _product.description ?? "";
    return {
      value: code,
      label: `${code} - ${description}`,
      description,
    };
  });

  const selectedOption =
    options.find((opt) => opt.value === product.product_code) ?? null;

  const [bulksInput, setBulksInput] = useState(String(product.bulks ?? 1));

  useEffect(() => {
    setBulksInput(String(product.bulks ?? 1));
  }, [product.bulks]);

  const handleBulksInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBulksInput(event.target.value);
  };

  const commitBulksChange = () => {
    const parsed = parseInt(bulksInput, 10);
    const val = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    if (String(val) !== bulksInput) {
      setBulksInput(String(val));
    }
    if (val === product.bulks) {
      return;
    }
    const unitsPerBulk = parseBulkMetric(product.units_per_bulk) || 1;
    updateMethod(id, {
      ...product,
      bulks: val,
      total_units: val * unitsPerBulk,
    });
  };

  const totalUnits =
    parseBulkMetric(product.units_per_bulk) * parseBulkMetric(product.bulks);

  // --- Datos de compra (almacén, costo, IVA) ---------------------------------
  const [unitCostInput, setUnitCostInput] = useState(
    product.unit_cost != null ? String(product.unit_cost) : "",
  );

  useEffect(() => {
    setUnitCostInput(product.unit_cost != null ? String(product.unit_cost) : "");
  }, [product.unit_cost]);

  const commitUnitCost = () => {
    const raw = unitCostInput.trim();
    if (raw === "") {
      if (product.unit_cost != null) {
        updateMethod(id, { ...product, unit_cost: null });
      }
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed) || parsed < 0) {
      // Valor inválido: se revierte a lo último bueno en vez de propagarlo.
      setUnitCostInput(
        product.unit_cost != null ? String(product.unit_cost) : "",
      );
      return;
    }
    if (parsed !== product.unit_cost) {
      updateMethod(id, { ...product, unit_cost: parsed });
    }
  };

  const handleAppliesTaxChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateMethod(id, { ...product, applies_tax: event.target.checked });
  };

  const missingCost = product.unit_cost == null || product.unit_cost <= 0;
  const missingWarehouse = !String(product.warehouse_code ?? "").trim();

  const lineWeightDisplay = lineWeightKg(
    product.weigth,
    product.bulks,
  ).toLocaleString("defaults", { maximumFractionDigits: 2 });

  const handleCodeChange = (event: ProductOption | null) => {
    if (!event?.value) {
      return;
    }
    const attrs = findProductByCode(selection, event.value);
    const pkg = attrs?.units?.find((unit) => unit.code === "BUL");
    if (!attrs || !pkg) {
      return;
    }
    let _product: IncomingProduct = {
      product_code: productErpCode(attrs),
      description: attrs.description,
      bulks: product.bulks,
      moq: attrs.attributes.filter(
        (attr) => attr.name.toLowerCase() === "moq"
      )[0].value,
      port: attrs.attributes.filter(
        (attr) => attr.name.toLowerCase() === "puerto 1"
      )[0].value,
      manufacturer: attrs.attributes.filter(
        (attr) => attr.name.toLowerCase() === "proveedor"
      )[0].value,
      cbm: bulkCbmPerUnit(pkg),
      units_per_bulk: pkg.units,
      weigth: bulkWeightPerUnit(pkg),
      unit_fob: fobPerUnitFromProduct(attrs),
    };
    updateMethod(id, _product);
  };

  return (
    <Tr fontSize="sm">
      <Td
        px="5px"
        w={PRODUCT_SELECT_COLUMN_WIDTH}
        minW={PRODUCT_SELECT_COLUMN_WIDTH}
        maxW={PRODUCT_SELECT_COLUMN_WIDTH}
        verticalAlign="top"
      >
        <Tooltip
          label={selectedOption?.label ?? product.product_code}
          placement="top"
          hasArrow
          openDelay={400}
          isDisabled={!selectedOption?.label}
        >
          <Box w="100%" minW={0}>
            <Select<ProductOption, false, GroupBase<ProductOption>>
              size="sm"
              options={options}
              value={selectedOption}
              formatOptionLabel={formatProductOptionLabel}
              maxMenuHeight={220}
              placeholder="Producto"
              isSearchable
              name="product_code"
              onChange={handleCodeChange}
              menuPlacement="auto"
              menuPosition="fixed"
              menuPortalTarget={productSelectMenuPortalTarget}
              styles={productSelectStyles}
              chakraStyles={productSelectChakraStyles}
            />
          </Box>
        </Tooltip>
      </Td>
      <Td px="5px">
        <Input
          type="number"
          value={bulksInput}
          variant="auth"
          textAlign="center"
          name="bulks"
          onChange={handleBulksInputChange}
          onBlur={commitBulksChange}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
          fontSize="xs"
          min={1}
        />
      </Td>
      <Td px="5px">
        <Text textAlign="center" w="100%" fontSize="xs">
          {product.moq}
        </Text>
      </Td>
      <Td px="5px">
        <Text
          textAlign="center"
          w="100%"
          fontSize="xs"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          title={totalUnits.toLocaleString("en-US")}
        >
          {totalUnits.toLocaleString("en-US")}
        </Text>
      </Td>
      <Td px="5px">
        <Text textAlign="center" w="100%" fontSize="xs">
          {formatMoney(product.unit_fob ?? 0)}
        </Text>
      </Td>
      <Td px="5px">
        <Text textAlign="center" w="100%" fontSize="xs">
          {(product.cbm * product.bulks).toLocaleString("defaults", {
            maximumFractionDigits: 2,
          })}
        </Text>
      </Td>
      <Td px="5px">
        <Text
          textAlign="center"
          w="100%"
          fontSize="xs"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          title={lineWeightDisplay}
        >
          {lineWeightDisplay}
        </Text>
      </Td>
      <Td px="5px">
        <Text textAlign="center" w="100%" fontSize="sm">
          {product.port}
        </Text>
      </Td>
      <Td px="5px">
        <Text
          textAlign="center"
          w="100%"
          fontSize="xs"
          maxW="75px"
          maxH="40px"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {product.manufacturer}
        </Text>
      </Td>
      {showPurchaseData ? (
        <>
          <Td px="5px">
            {/*
              El almacén sale del maestro de atributos del producto. Se deja
              editable como salida de emergencia —un material recién cargado sin
              la columna llena bloquearía la orden entera— pero el valor no se
              elige de una lista: escribir a mano es justamente el camino que
              produce "No existen almacenes con código: ZPR1.".
            */}
            <Input
              size="xs"
              variant="auth"
              fontSize="xs"
              textAlign="center"
              value={product.warehouse_code ?? ""}
              placeholder="del maestro"
              borderColor={missingWarehouse ? "orange.400" : undefined}
              onChange={(event) =>
                updateMethod(id, {
                  ...product,
                  warehouse_code: event.target.value,
                })
              }
            />
          </Td>
          <Td px="5px">
            <Input
              size="xs"
              type="number"
              step="0.01"
              min={0}
              variant="auth"
              fontSize="xs"
              textAlign="center"
              value={unitCostInput}
              placeholder="0.00"
              borderColor={missingCost ? "orange.400" : undefined}
              onChange={(event) => setUnitCostInput(event.target.value)}
              onBlur={commitUnitCost}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.currentTarget.blur();
                }
              }}
            />
          </Td>
          <Td px="5px" textAlign="center">
            <Checkbox
              size="sm"
              isChecked={product.applies_tax !== false}
              onChange={handleAppliesTaxChange}
            />
          </Td>
        </>
      ) : null}
      <Td px="5px" textAlign="center">
        <Icon
          as={IoIosRemoveCircle}
          color="red.500"
          onClick={() => removeMethod(id)}
        />
      </Td>
    </Tr>
  );
};

const BuyOrderCollapse = (props: {
  id: number;
  order: BuyOrder;
  products: ProductProps[];
  removeMethod: (order: BuyOrder) => void;
  updateMethod: (number: string, newOrder: BuyOrder) => void;
  profitStatus?: ProfitPushStatus | null;
  /** Bloqueo global: todavía no se sabe qué se envió, o la consulta falló. */
  isStateUnknown?: boolean;
  /**
   * Reporta al padre si el autocompletado de datos de compra (almacén,
   * costo) de esta orden sigue en vuelo, para que pueda bloquear los botones
   * que persisten renglones mientras dure.
   */
  onPurchaseDataPendingChange?: (orderId: string, isPending: boolean) => void;
}) => {
  const {
    order,
    products,
    removeMethod,
    updateMethod,
    profitStatus,
    isStateUnknown,
    onPurchaseDataPendingChange,
  } = props;
  // `editable` viene resuelto del backend. Bloquear acá es UX; la regla real la
  // aplican los serializers, porque un request directo saltaría esto.
  //
  // Mientras el estado no se conoce también se bloquea: renderizar todo
  // editable y bloquear después deja una ventana en la que se puede empezar a
  // escribir en una orden que ya está en el ERP.
  const isLockedByProfit =
    Boolean(isStateUnknown) || Boolean(profitStatus && !profitStatus.editable);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newProducts, setProducts] = useState(order.incoming_product_list);
  const [reloadTable, setReloadTable] = useState(Math.random());

  useEffect(() => {
    setProducts(order.incoming_product_list ?? []);
    setReloadTable(Math.random());
  }, [order.incoming_product_list, order.number, order.id]);

  const orderBgColor = useColorModeValue("whiteAlpha.500", "#3d4756");
  const dataBgColor = useColorModeValue("#2c333e80", "#2c333e80");
  const productBgColor = useColorModeValue("#2e394a", "#2e394a");

  const pdfToast = useToast();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const canDownloadPdf = isPersistedSupplyDocumentOrder(order);
  const canSaveAudit = canDownloadPdf;

  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [isSavingAudit, setIsSavingAudit] = useState(false);

  const canSendEmail = canDownloadPdf;
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  // "pendiente" = todavía no se intentó en esta sesión del modal; no persiste
  // entre aperturas porque el backend no expone hoy el último estado de envío.
  const [emailStatus, setEmailStatus] = useState<
    "pendiente" | "enviado" | "fallido"
  >("pendiente");

  // --- Datos de compra -------------------------------------------------------
  // Ocultos por defecto: quien sólo simula cobertura no necesita ver costos, y
  // el drawer mide 560 px. Se piden al datamart recién cuando se abre el panel.
  const [showPurchaseData, setShowPurchaseData] = useState(false);
  const productCodes = (newProducts ?? [])
    .map((line) => String(line.product_code ?? "").trim())
    .filter(Boolean);
  // Se piden SIEMPRE, no sólo con el panel abierto: proveedor y almacén salen
  // del maestro y no cuestan una llamada al datamart. Cuando dependían del
  // panel, una orden guardada sin abrirlo llegaba a Profit sin proveedor y el
  // rechazo aparecía recién al enviar.
  const purchaseDefaults = usePurchaseDefaults(true, productCodes);
  const prefilledRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (purchaseDefaults.isLoading) {
      return;
    }
    let changed = false;
    const next = (newProducts ?? []).map((line) => {
      const code = String(line.product_code ?? "").trim();
      const defaults = purchaseDefaults.byCode[code];
      // Una sola pasada por código: si el usuario borra un valor prellenado, no
      // se lo volvemos a escribir en el render siguiente.
      if (!code || !defaults || prefilledRef.current.has(code)) {
        return line;
      }
      prefilledRef.current.add(code);
      const patch: Partial<IncomingProduct> = {};
      if (line.unit_cost == null && defaults.unit_cost != null) {
        patch.unit_cost = defaults.unit_cost;
      }
      // El almacén viene resuelto del backend: primero la columna del maestro
      // de productos, y sólo si ahí no está, el único almacén del datamart.
      if (!String(line.warehouse_code ?? "").trim() && defaults.warehouse_code) {
        patch.warehouse_code = defaults.warehouse_code;
      }
      if (!String(line.uom ?? "").trim() && defaults.uom) {
        patch.uom = defaults.uom;
      }
      if (Object.keys(patch).length === 0) {
        return line;
      }
      changed = true;
      return { ...line, ...patch };
    });
    if (changed) {
      setProducts(next);
      updateMethod(order.id, { ...order, incoming_product_list: next });
    }
  }, [purchaseDefaults, newProducts, order, updateMethod]);

  /**
   * Proveedor de la orden, **derivado de sus productos**.
   *
   * Por regla de negocio un producto lo vende un solo proveedor, así que el
   * proveedor de la OC no es algo que se elija: sale del maestro, igual que el
   * almacén. Antes esto era un selector alimentado por el catálogo del
   * datamart, que es una fuente distinta y además casi vacía — un proveedor
   * para 300 materiales.
   *
   * Si los materiales de la orden traen proveedores distintos, no se elige
   * ninguno: por la misma regla, esa orden **está mal armada** y mezcla
   * mercancía de dos proveedores. Elegir uno en silencio le pediría a alguien
   * productos que no vende.
   */
  const vendorCodes = Array.from(
    new Set(
      productCodes
        .map((code) => purchaseDefaults.byCode[code]?.vendor_code ?? "")
        .filter(Boolean),
    ),
  );
  const masterVendorCode = vendorCodes.length === 1 ? vendorCodes[0] : "";
  const vendorsDisagree = vendorCodes.length > 1;
  const masterVendorName = (() => {
    for (const code of productCodes) {
      const entry = purchaseDefaults.byCode[code];
      if (entry?.vendor_code && entry.vendor_code === masterVendorCode) {
        return entry.vendor_name ?? "";
      }
    }
    return "";
  })();

  // Se escribe en la orden apenas se conoce, sin depender de que el usuario
  // abra el panel: es lo que `createPurchasingOrder` exige y no hay nada que
  // decidir.
  useEffect(() => {
    if (purchaseDefaults.isLoading || !masterVendorCode) {
      return;
    }
    if (String(order.vendor_code ?? "").trim() === masterVendorCode) {
      return;
    }
    updateMethod(order.id, { ...order, vendor_code: masterVendorCode });
  }, [purchaseDefaults.isLoading, masterVendorCode, order, updateMethod]);

  /**
   * Le avisa al padre si el GET de purchase-defaults sigue en vuelo para esta
   * orden. Declarado después de los dos efectos de arriba a propósito: React
   * corre los efectos de un componente en el orden en que están escritos, así
   * que cuando `isLoading` pasa a `false` el efecto que aplica el parche
   * (almacén/costo) y el del proveedor ya alcanzaron a llamar a `updateMethod`
   * en este mismo commit — el padre nunca desbloquea Guardar con datos a
   * medio escribir.
   *
   * El cleanup limpia el pendiente al desmontar: si la orden se elimina
   * mientras el fetch está en vuelo, el padre no debe quedar bloqueado por
   * una orden que ya no existe.
   */
  useEffect(() => {
    onPurchaseDataPendingChange?.(order.id, purchaseDefaults.isLoading);
    return () => onPurchaseDataPendingChange?.(order.id, false);
  }, [purchaseDefaults.isLoading, order.id, onPurchaseDataPendingChange]);

  const handleDownloadPdf = async () => {
    const documentId = String(order.id ?? "").trim();
    if (!documentId) return;
    setIsDownloadingPdf(true);
    try {
      await apiDownloadPurchaseOrderPdf(documentId, order.number);
    } catch (err) {
      pdfToast({
        title: "No se pudo generar el PDF",
        description:
          "Ocurrió un error al generar el PDF de la orden de compra. Intenta nuevamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleSaveAudit = async (folderId: string, folderName: string) => {
    const documentId = String(order.id ?? "").trim();
    if (!documentId) return;
    setIsSavingAudit(true);
    try {
      await apiSavePurchaseOrderAudit(documentId, folderId, folderName);
      pdfToast({
        title: "Auditoría guardada en Drive",
        description: `Se archivaron el PDF y el Excel de la orden #${order.number} en "${folderName}".`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setIsAuditOpen(false);
    } catch (err) {
      pdfToast({
        title: "No se pudo guardar la auditoría",
        description:
          "Ocurrió un error al archivar el PDF en Drive. Intenta nuevamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingAudit(false);
    }
  };

  const handleSendEmail = async () => {
    const documentId = String(order.id ?? "").trim();
    if (!documentId) return;
    setIsSendingEmail(true);
    try {
      const result = await apiSendPurchaseOrderEmail(documentId);
      setEmailStatus("enviado");
      pdfToast({
        title: "Orden enviada al proveedor",
        description: `Se envió la OC #${order.number} a ${result.recipient}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      setEmailStatus("fallido");
      const body = (err as { response?: { status?: number; data?: SendPurchaseOrderEmailErrorBody } })
        ?.response;
      const isMissingRecipient = body?.status === 422;
      pdfToast({
        title: isMissingRecipient
          ? "El proveedor no tiene correo configurado"
          : "No se pudo enviar el correo",
        description:
          body?.data?.detail ??
          (isMissingRecipient
            ? "Cargá el 'Email Contacto Proveedor' del producto antes de reintentar."
            : "Ocurrió un error al enviar la orden de compra por correo. Intenta nuevamente."),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Replace the existing addNewProductRow function inside BuyOrderCollapse with this:
  const addNewProductRow = () => {
    // --- DEFENSIVE CHECK 1: Ensure the products prop array is not empty ---
    // 'products' here is the prop passed to BuyOrderCollapse, listing all available products
    if (!products || products.length === 0) {
      console.error("Cannot add new product row: The 'products' selection list is empty.");
      // Optionally: Display a user-friendly error message using state and an alert component
      // Example: setErrorState("No base products available to add.");
      return; // Stop execution if no products are available
    }

    // Use the first product from the available list as the default for the new row
    const defaultProductData = products[0];

    // --- DEFENSIVE CHECK 2: Find the 'BUL' unit safely ---
    // Use optional chaining (?.) in case 'units' itself is missing (though TS might prevent this)
    // Use .find() as it's slightly more direct than filter(...)[0] for getting one item
    const pkg = defaultProductData.units?.find((unit) => unit.code === "BUL");

    // Check if the 'BUL' package was found
    if (!pkg) {
      console.error(
        `Cannot add product row: Default product ${defaultProductData.erp_code} does not have a unit with code 'BUL'.`
      );
      // Optionally: Display a user-friendly error message
      // Example: setErrorState(`Product ${defaultProductData.erp_code} is missing required 'BUL' unit info.`);
      return; // Stop execution if the required package info is missing
    }

    // --- DEFENSIVE CHECK 3: Find attributes safely ---
    // Helper function to find an attribute's value or return a default
    const findAttributeValue = (
      attributeName: string,
      defaultValue: string | number | undefined = undefined // Allow specifying a default
    ) => {
      const attribute = defaultProductData.attributes?.find(
        (attr) => attr.name.toLowerCase() === attributeName.toLowerCase()
      );
      // Return the found attribute's value, or the provided default, or undefined if no default
      return attribute ? attribute.value : defaultValue;
    }

    // Get attribute values, providing sensible defaults if they might be missing
    const moq = findAttributeValue("moq", 1); // Default MOQ to 1 if not found
    const port = findAttributeValue("puerto 1", "N/A"); // Default Port to "N/A" if not found
    const manufacturer = findAttributeValue("proveedor", "Unknown"); // Default Manufacturer if not found

    // Optional: Log warnings if defaults were used (useful during development)
    if (findAttributeValue("moq") === undefined) {
      console.warn(`Default product ${defaultProductData.erp_code} is missing 'moq' attribute. Using default: ${moq}`);
    }
    if (findAttributeValue("puerto 1") === undefined) {
      console.warn(`Default product ${defaultProductData.erp_code} is missing 'puerto 1' attribute. Using default: ${port}`);
    }
    if (findAttributeValue("proveedor") === undefined) {
      console.warn(`Default product ${defaultProductData.erp_code} is missing 'proveedor' attribute. Using default: ${manufacturer}`);
    }

    // --- Create the new product object (now using safe values) ---
    // This is the 'obj' variable from your original code
    const newIncomingProduct: IncomingProduct = {
      product_code: defaultProductData.erp_code, // Safe: checked products[0] exists
      description: defaultProductData.description,
      moq: moq, // Safe: uses found value or default
      bulks: 1, // Sensible default for a newly added row
      port: port, // Safe: uses found value or default
      manufacturer: manufacturer, // Safe: uses found value or default
      // These are safe because we checked 'pkg' exists before getting here
      cbm: bulkCbmPerUnit(pkg),
      weigth: bulkWeightPerUnit(pkg),
      // Use the units_per_bulk from pkg for initial calculation based on 1 bulk
      total_units: pkg.units * 1, // total_units = units_per_bulk * bulks (which is 1 initially)
      units_per_bulk: pkg.units,
      unit_fob: fobPerUnitFromProduct(defaultProductData),
    };

    // --- Update State (using immutable patterns) ---
    // Create a completely new array instead of pushing to the existing state array
    const updatedProductsList = [...newProducts, newIncomingProduct];

    // Update the local state for the product list within this collapse component
    setProducts(updatedProductsList);

    // Create a completely new order object containing the updated product list
    // 'order' here is the prop passed to BuyOrderCollapse
    const updatedOrderData: BuyOrder = {
      ...order, // Copy all properties from the original order prop
      incoming_product_list: updatedProductsList, // Override with the new list
    };

    // Call the update method passed from the parent (OrderEditorModal)
    // Pass the order's identifier (number or id) and the *new* order data object
    updateMethod(order.id, updatedOrderData); // updateMethod busca por id, no por number (editable por el usuario)

    // Consider removing setReloadTable(Math.random()) if the component re-renders
    // correctly just from the 'setProducts' state update. Test this.
    // setReloadTable(Math.random());
  };

  const removeNewProduct = (idx: number) => {
    let _products = newProducts;
    _products = _products.filter((_prd) => _prd !== _products[idx]);
    let _order = order;
    _order.incoming_product_list = _products;
    updateMethod(order.id, _order);
    setProducts(_products);
    setReloadTable(Math.random());
  };

  const updateProductRow = (idx: number, newProduct: IncomingProduct) => {
    const updatedProductsList = newProducts.map((row, index) =>
      index === idx ? newProduct : row,
    );
    const updatedOrder: BuyOrder = {
      ...order,
      incoming_product_list: updatedProductsList,
    };
    updateMethod(order.id, updatedOrder);
    setProducts(updatedProductsList);
  };

  const toggleCollapse = () => {
    if (isOpen) {
      onClose();
      // ...
    } else {
      onOpen();
    }
  };

  const getProductRows = () => {
    let bulks_total = 0
    let units_total = 0
    let fob_total = 0
    let cbm_total = 0
    let weigth_total = 0
    return (
      <Table
        w="100%"
        minW={ORDER_LINES_TABLE_MIN_WIDTH}
        sx={{ tableLayout: "fixed" }}
      >
        <colgroup>
          <col style={{ width: PRODUCT_SELECT_COLUMN_WIDTH }} />
          <col style={{ width: "72px" }} />
          <col style={{ width: "64px" }} />
          <col style={{ width: "80px" }} />
          <col style={{ width: "80px" }} />
          <col style={{ width: "64px" }} />
          <col style={{ width: "80px" }} />
          <col style={{ width: "72px" }} />
          <col style={{ width: "90px" }} />
          {showPurchaseData ? (
            <>
              <col style={{ width: "84px" }} />
              <col style={{ width: "80px" }} />
              <col style={{ width: "44px" }} />
            </>
          ) : null}
          <col style={{ width: "36px" }} />
        </colgroup>
        <Thead
          position="sticky"
          top="0px"
          bgColor={productBgColor}
          zIndex={1}
          boxShadow={`inset 0px -1.5px #3d4756`}
          pt="20px"
        >
          <Tr>
            <Th textAlign="center" px="5px">
              Producto
            </Th>
            <Th textAlign="center" px="5px">
              Bultos
            </Th>
            <Th textAlign="center" px="5px">
              MOQ
            </Th>
            <Th textAlign="center" px="5px">
              Und.
            </Th>
            <Th textAlign="center" px="5px">
              FOB x UND
            </Th>
            <Th textAlign="center" px="5px">
              CBM
            </Th>
            <Th textAlign="center" px="5px">
              Peso (KG)
            </Th>
            <Th textAlign="center" px="5px">
              Puerto
            </Th>
            <Th textAlign="center" px="5px">
              Proveedor
            </Th>
            {showPurchaseData ? (
              <>
                <Th textAlign="center" px="5px">
                  Almacén
                </Th>
                <Th textAlign="center" px="5px">
                  Costo
                </Th>
                <Th textAlign="center" px="5px">
                  IVA
                </Th>
              </>
            ) : null}
            <Th textAlign="center" px="5px">
              -
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {newProducts.map((product, rowIndex) => {
            const attrs = findProductByCode(products, product.product_code);
            const pkg = attrs?.units?.find((unit) => unit.code === "BUL");
            const unitFobFromAttrs =
              product.unit_fob != null
                ? product.unit_fob
                : attrs
                  ? fobPerUnitFromProduct(attrs)
                  : product.unit_fob ?? 0;
            const instance: IncomingProduct =
              attrs && pkg
                ? {
                  product_code: product.product_code,
                  total_units: product.total_units,
                  bulks: product.bulks,
                  moq:
                    attrs.attributes?.find(
                      (attr) => attr.name.toLowerCase() === "moq"
                    )?.value ??
                    product.moq ??
                    "",
                  port:
                    attrs.attributes?.find(
                      (attr) => attr.name.toLowerCase() === "puerto 1"
                    )?.value ??
                    product.port ??
                    "",
                  manufacturer:
                    attrs.attributes?.find(
                      (attr) => attr.name.toLowerCase() === "proveedor"
                    )?.value ??
                    product.manufacturer ??
                    "",
                  cbm: bulkCbmPerUnit(pkg),
                  weigth: bulkWeightPerUnit(pkg),
                  units_per_bulk: pkg.units,
                  unit_fob: unitFobFromAttrs,
                  // Sin esto, cada render reconstruye la fila y borra lo que el
                  // usuario acaba de cargar en almacén/costo/IVA.
                  unit_cost: product.unit_cost ?? null,
                  warehouse_code: product.warehouse_code ?? "",
                  applies_tax: product.applies_tax,
                  tax_pctg: product.tax_pctg ?? null,
                  discount_pctg: product.discount_pctg ?? null,
                  uom: product.uom ?? "",
                }
                : {
                  product_code: product.product_code,
                  description: product.description,
                  total_units:
                    product.total_units ??
                    (product.bulks ?? 1) * (product.units_per_bulk ?? 1),
                  bulks: product.bulks ?? 1,
                  moq: product.moq ?? "",
                  port: product.port ?? "",
                  manufacturer: product.manufacturer ?? "",
                  cbm: parseBulkMetric(product.cbm),
                  weigth: parseBulkMetric(product.weigth),
                  units_per_bulk: product.units_per_bulk ?? 1,
                  unit_fob: unitFobFromAttrs,
                  unit_cost: product.unit_cost ?? null,
                  warehouse_code: product.warehouse_code ?? "",
                  applies_tax: product.applies_tax,
                  tax_pctg: product.tax_pctg ?? null,
                  discount_pctg: product.discount_pctg ?? null,
                  uom: product.uom ?? "",
                };
            const unitWeight = bulkWeightPerUnit(pkg) || parseBulkMetric(product.weigth);
            const weightContribution = lineWeightKg(unitWeight, instance.bulks);
            const unitFob = unitFobFromAttrs;
            bulks_total += instance.bulks
            units_total += instance.total_units
            fob_total += unitFob * instance.total_units
            cbm_total += parseBulkMetric(instance.cbm) * instance.bulks
            weigth_total += weightContribution
            return (
              <IncomingProductRow
                key={`${product.product_code}-${rowIndex}`}
                id={rowIndex}
                product={instance}
                selection={products}
                removeMethod={removeNewProduct}
                updateMethod={updateProductRow}
                showPurchaseData={showPurchaseData}
                purchaseDefaults={purchaseDefaults.byCode[product.product_code]}
              />
            );
          })}
        </Tbody>
        {newProducts[0] ?
          <Tfoot
            position="sticky"
            bottom="-5px"
            fontSize='sm'
            bgColor={productBgColor}
            zIndex={0}
            boxShadow={`inset 0px 1.5px 0px 0px #3d4756`}
          >
            <Tr>
              <Td></Td>
              <Td textAlign={'center'}>{bulks_total}</Td>
              <Td></Td>
              <Td textAlign="center" whiteSpace="nowrap">
                {units_total.toLocaleString("en-US")}
              </Td>
              <Td textAlign={'center'}>{formatMoney(fob_total)}</Td>
              <Td textAlign={'center'}>{(cbm_total).toLocaleString("defaults", {
                maximumFractionDigits: 2,
              })}</Td>
              <Td textAlign="center" whiteSpace="nowrap">
                {weigth_total.toLocaleString("defaults", {
                  maximumFractionDigits: 2,
                })}
              </Td>
              <Td></Td>
              <Td></Td>
              {showPurchaseData ? (
                <>
                  <Td></Td>
                  <Td></Td>
                  <Td></Td>
                </>
              ) : null}
              <Td></Td>
            </Tr>
          </Tfoot> : null
        }
      </Table>
    );
  };

  const handleAttrChange = (event: any) => {
    let attr = event.target.name;
    let _order = order;
    let val = event.target.value;
    if (attr === "ata" || attr === "ataw") {
      val = new Date(val);
      val = new Date(val.setDate(val.getDate() + 1));
    }
    _order[attr] = val;
    updateMethod(order.id, _order);
  };

  return (
    <>
      <Box w="100%" bgColor={orderBgColor} borderRadius="30px">
        <Flex w="100%">
          <Button onClick={toggleCollapse}>
            <Icon as={MdModeEdit} />
          </Button>
          <Text h="100%" alignSelf="center" ml="15px">
            {`Orden #${order.number} (${new Date(
              order.ata
            ).toLocaleDateString()})`}
          </Text>
          <Flex flex="1" alignSelf="center" ml="10px">
            <ProfitStatusBadge status={profitStatus} />
          </Flex>
          {canDownloadPdf ? (
            <Tooltip label="Descargar PDF de la orden de compra">
              <Button
                onClick={handleDownloadPdf}
                isLoading={isDownloadingPdf}
                aria-label="Descargar PDF"
              >
                <Icon as={MdDownload} />
              </Button>
            </Tooltip>
          ) : null}
          {canSaveAudit ? (
            <Tooltip label="Agregar auditoría en Drive">
              <Button
                onClick={() => setIsAuditOpen(true)}
                isLoading={isSavingAudit}
                aria-label="Agregar auditoría"
              >
                <Icon as={MdCloudUpload} />
              </Button>
            </Tooltip>
          ) : null}
          {canSendEmail ? (
            <Tooltip
              label={
                emailStatus === "enviado"
                  ? "Enviado al proveedor"
                  : emailStatus === "fallido"
                    ? "Falló el envío — reintentar"
                    : "Enviar a proveedor"
              }
            >
              <Button
                onClick={handleSendEmail}
                isLoading={isSendingEmail}
                aria-label="Enviar a proveedor"
              >
                <Icon
                  as={MdOutlineMailOutline}
                  color={
                    emailStatus === "enviado"
                      ? "green.400"
                      : emailStatus === "fallido"
                        ? "red.400"
                        : undefined
                  }
                />
              </Button>
            </Tooltip>
          ) : null}
          <Button
            onClick={() => removeMethod(order)}
            isDisabled={isLockedByProfit}
          >
            <Icon as={IoIosRemoveCircle} color="red.500" />
          </Button>
        </Flex>
      </Box>
      <Collapse in={isOpen} animateOpacity style={{ width: "100%" }}>
        <Box bgColor={dataBgColor} p="10px" borderRadius="20px">
          {profitStatus && profitStatus.message ? (
            <CustomAlert
              // El mensaje del datamart va tal cual: "No existen almacenes con
              // código: ZPR1." dice qué corregir; "Ocurrió un error" no.
              message={profitStatus.message}
              status={profitStatus.editable ? "warning" : "info"}
              mb="10px"
              borderRadius="16px"
            />
          ) : null}
          {/*
            `fieldset[disabled]` deshabilita todos los controles que contiene,
            incluido el acceso por teclado — a diferencia de `pointer-events`,
            que sólo tapa el mouse. `minWidth: 0` es obligatorio: el ancho
            intrínseco del fieldset rompe el layout de la tabla si no.
          */}
          <fieldset
            disabled={isLockedByProfit}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
          <FormLabel
            display="flex"
            ms="4px"
            fontSize="sm"
            fontWeight="500"
            mb="8px"
          >
            Número y Fecha de Llegada (ATA):
          </FormLabel>
          <InputGroup size="md" mb="15px">
            <Input
              type="text"
              defaultValue={order.number}
              name='number'
              mr="10px"
              variant="auth"
              fontSize="sm"
              onChange={handleAttrChange}
            />
            <Input
              type="date"
              name="ata"
              defaultValue={toDateInputValue(order.ata)}
              variant="auth"
              fontSize="sm"
              onChange={handleAttrChange}
            />
          </InputGroup>
          <FormLabel
            display="flex"
            ms="4px"
            fontSize="sm"
            fontWeight="500"
            mb="8px"
          >
            ATAW:
          </FormLabel>
          <InputGroup size="md" mb="15px">
            <Input
              type="date"
              name="ataw"
              defaultValue={toDateInputValue(order.ataw ?? order.ata)}
              variant="auth"
              fontSize="sm"
              onChange={handleAttrChange}
            />
          </InputGroup>
          <Flex alignItems="center" justifyContent="space-between" mb="10px">
            <FormLabel fontSize="sm" fontWeight="500" mb="0px" ms="4px">
              Datos de compra (Profit)
            </FormLabel>
            <Switch
              size="sm"
              isChecked={showPurchaseData}
              onChange={(event) => setShowPurchaseData(event.target.checked)}
            />
          </Flex>
          {showPurchaseData ? (
            <Box mb="15px">
              <FormLabel fontSize="sm" fontWeight="500" mb="8px" ms="4px">
                Proveedor (código ERP):
              </FormLabel>
              {/*
                No es un selector: por regla de negocio un producto lo vende un
                solo proveedor, así que el de la orden sale del maestro y no hay
                nada que elegir. Se muestra para que se vea qué va a recibir el
                ERP, no para editarlo.
              */}
              <Flex
                align="center"
                gap="8px"
                bg={productBgColor}
                borderRadius="12px"
                px="12px"
                py="8px"
              >
                {order.vendor_code ? (
                  <>
                    <Text fontSize="sm" fontWeight="600">
                      {order.vendor_code}
                    </Text>
                    {masterVendorName ? (
                      <Text fontSize="xs" opacity={0.75}>
                        {masterVendorName}
                      </Text>
                    ) : null}
                    <Text fontSize="10px" opacity={0.6} ms="auto">
                      del maestro de productos
                    </Text>
                  </>
                ) : (
                  <Text fontSize="sm" color="orange.300">
                    {purchaseDefaults.isLoading
                      ? "Cargando…"
                      : "Sin proveedor en el maestro de productos"}
                  </Text>
                )}
              </Flex>
              {vendorsDisagree ? (
                <CustomAlert
                  message={
                    "Los materiales de esta orden tienen proveedores distintos " +
                    "en el maestro. Una orden de compra va a un solo proveedor: " +
                    "separá los materiales en órdenes distintas."
                  }
                  status="warning"
                  mt="8px"
                  borderRadius="12px"
                />
              ) : null}
              {!purchaseDefaults.isLoading && !order.vendor_code ? (
                <CustomAlert
                  message={
                    "Ningún material de esta orden trae el código de proveedor " +
                    "del ERP. Completá la columna «Código Proveedor ERP» del " +
                    "maestro de productos y volvé a cargarlo."
                  }
                  status="warning"
                  mt="8px"
                  borderRadius="12px"
                />
              ) : null}
              {!purchaseDefaults.datamartAvailable ? (
                // El proveedor y el almacén siguen ahí: salen del maestro. Lo
                // que se pierde es el precio negociado, así que el aviso no
                // debe sonar a que falta lo esencial.
                <CustomAlert
                  message={
                    "Sin conexión al datamart: los costos son los del maestro " +
                    "y no reflejan el precio negociado con el proveedor. " +
                    "Verificá el costo antes de cargar la orden en Profit."
                  }
                  status="info"
                  mt="8px"
                  borderRadius="12px"
                />
              ) : null}
            </Box>
          ) : null}
          <Flex justifyContent="space-between" mb="10px">
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              mb="8px"
            >
              Productos:
            </FormLabel>
            <Button onClick={addNewProductRow} p="0px" h="29px" w="29px">
              <Icon as={MdAddCircle} color="green.500" />
            </Button>
          </Flex>
          <Box
            ps="10px"
            py={0}
            bgColor={productBgColor}
            borderRadius="15px"
            h={{ base: "240px", "2xl": "320px" }}
            overflowX="auto"
            overflowY="auto"
          >
            {getProductRows()}
          </Box>
          </fieldset>
        </Box>
      </Collapse>
      {canSaveAudit ? (
        <DriveFolderPickerModal
          isOpen={isAuditOpen}
          onClose={() => setIsAuditOpen(false)}
          onConfirm={handleSaveAudit}
          isSaving={isSavingAudit}
          orderNumber={order.number}
        />
      ) : null}
    </>
  );
};

export const OrderEditorModal = (props: {
  orders: BuyOrder[];
  products: ProductProps[];
  simulationId: string | null;
  reloader: (value: number) => void;
  modalDisclosure: any;
}) => {
  const { orders, products, simulationId, reloader, modalDisclosure } = props;
  const { modalIsOpen, modalOnClose } = modalDisclosure;
  const drawerSurfaceBg = useColorModeValue("white", "navy.800");
  const drawerSubtitleColor = useColorModeValue("gray.600", "gray.400");
  const [stackReload, setStackReload] = useState(Math.random());
  const [patchError, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isRemovingOrder, setIsRemovingOrder] = useState(false);
  const [newOrders, setNewOrders] = useState(orders);
  const [helperIsOpen, setOpenHelper] = useState(newOrders.length === 0);
  const [isLoadingSimulationOrders, setIsLoadingSimulationOrders] = useState(false);
  const [isPushingProfit, setIsPushingProfit] = useState(false);
  const [isProfitConfirmOpen, setIsProfitConfirmOpen] = useState(false);
  // Una llamada por simulación, con refresco contra el datamart al abrir y
  // polling ligero mientras quede alguna orden sin resolver.
  const profitPushes = useProfitPushStatus(simulationId, modalIsOpen);
  const profitStatuses = profitPushes.byOrderId;

  // Por orden: si su GET de purchase-defaults sigue en vuelo, todavía no se
  // sabe si el renglón recién agregado va a terminar con almacén/costo del
  // maestro. Guardar/Cargar en Profit/Agregar a Monday mandan lo que haya en
  // `newOrders` en el momento del click, así que persistir con esto en
  // `true` es la misma condición de carrera que dejaba renglones con
  // `unit_cost`/`warehouse_code` vacíos en la base.
  const [pendingPurchaseDataByOrder, setPendingPurchaseDataByOrder] = useState<
    Record<string, boolean>
  >({});
  const handlePurchaseDataPendingChange = useCallback(
    (orderId: string, isPending: boolean) => {
      setPendingPurchaseDataByOrder((prev) => {
        if (Boolean(prev[orderId]) === isPending) {
          return prev;
        }
        return { ...prev, [orderId]: isPending };
      });
    },
    [],
  );
  const hasPendingPurchaseData = Object.values(
    pendingPurchaseDataByOrder,
  ).some(Boolean);

  const loadSimulationPurchaseOrders = useCallback(async () => {
    if (!simulationId) {
      return;
    }
    setIsLoadingSimulationOrders(true);
    try {
      const supplyDocs = await apiListSupplyDocumentsForSimulation(simulationId);
      const fromSupplyDocuments = mapSupplyDocumentsToBuyOrders(
        supplyDocs,
        products,
      );

      if (fromSupplyDocuments.length > 0) {
        const merged = mergeSessionOrdersWithSupplyDocuments(
          orders,
          fromSupplyDocuments,
        );
        const finalOrders = merged.length > 0 ? merged : fromSupplyDocuments;
        setNewOrders(finalOrders);
        setOpenHelper(finalOrders.length === 0);
        return;
      }

      const hasSessionOrdersWithLines = orders.some(
        (order) => (order.incoming_product_list?.length ?? 0) > 0,
      );
      if (orders.length > 0 && hasSessionOrdersWithLines) {
        setNewOrders(orders);
        setOpenHelper(false);
        return;
      }

      if (orders.length > 0) {
        setNewOrders(orders);
        setOpenHelper(false);
        return;
      }

      const detail = await apiGetSimulationDetail(simulationId);
      const fromSimulation = purchaseOrderRefsToBuyOrders(
        detail.simulated_purchase_orders ?? [],
      );
      setNewOrders(fromSimulation);
      setOpenHelper(fromSimulation.length === 0);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingSimulationOrders(false);
    }
  }, [simulationId, orders, products]);

  useEffect(() => {
    if (modalIsOpen) {
      return;
    }
    if (orders) {
      setNewOrders(orders);
      setOpenHelper(orders.length === 0);
    }
  }, [orders, simulationId, modalIsOpen]);

  useEffect(() => {
    if (!modalIsOpen || !simulationId) {
      return;
    }
    if (orders.length > 0) {
      setNewOrders(orders);
      setOpenHelper(false);
    }
    loadSimulationPurchaseOrders();
  }, [modalIsOpen, simulationId, loadSimulationPurchaseOrders, orders, products.length]);


  const addNewOrder = () => {
    let orders = newOrders;
    let randomId = `MLS-${Math.floor(Math.random() * 10000)}`
    let order: BuyOrder = {
      id: randomId,
      number: randomId,
      ata: new Date(),
      ataw: new Date(),
      incoming_product_list: [],
    };
    orders.push(order);
    setNewOrders(orders);
    setStackReload(Math.random());
  };

  const removeOrder = async (order: BuyOrder) => {
    const blockedMessage = buyOrderDeleteBlockedMessage(order);
    if (blockedMessage) {
      toast({
        title: "No se puede eliminar",
        description: blockedMessage,
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setIsRemovingOrder(true);
    try {
      const persistedId = String(order.id ?? "").trim();
      if (/^\d+$/.test(persistedId)) {
        await apiDeleteSupplyDocument(persistedId);
      }
      const remaining = newOrders.filter((_order) => _order !== order);
      setOpenHelper(remaining.length === 0);
      setNewOrders(remaining);
      await ApiUpdateOrderList(remaining);
      reloader(Math.random());
    } catch (err) {
      const message = formatSaveError(err);
      toast({
        title: "No se pudo eliminar la orden",
        description:
          message.includes(REAL_PURCHASE_ORDER_MESSAGE)
            ? REAL_PURCHASE_ORDER_MESSAGE
            : message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsRemovingOrder(false);
    }
  };

  const updateOrder = (id: string, newOrder: BuyOrder) => {
    let orders = newOrders;
    let order = orders.filter((_order) => _order.id === id)[0];
    let idx = orders.indexOf(order);
    orders[idx] = newOrder;
    setNewOrders(orders);
  };

  const getNewOrders = () => {
    return (
      <VStack key={stackReload} spacing="15px">
        {newOrders.map((order, index) => (
          <BuyOrderCollapse
            order={order}
            key={`${order.id}-${order.number}-${order.incoming_product_list?.length ?? 0}`}
            id={index}
            removeMethod={removeOrder}
            updateMethod={updateOrder}
            products={products}
            profitStatus={profitStatuses[String(order.id ?? "").trim()]}
            isStateUnknown={isProfitStateUnknown}
            onPurchaseDataPendingChange={handlePurchaseDataPendingChange}
          />
        ))}
        <Button onClick={() => addNewOrder()} w="100%" colorScheme="green">
          <Icon as={MdAddCircle} color="green.500" />
          <Text ml="10px">Agregar Orden</Text>
        </Button>
      </VStack>
    );
  };

  const formatSaveError = (err: unknown): string => {
    const response = (err as { response?: { status?: number; data?: unknown } })
      ?.response;
    if (!response) {
      return "No se pudo guardar las órdenes de compra. Inténtelo de nuevo.";
    }
    if (response.status === 409) {
      const data = response.data;
      if (data && typeof data === "object" && "errors" in data) {
        const errors = (data as { errors: Array<{ detail?: string }> }).errors;
        const first = errors?.[0];
        if (first?.detail) {
          return first.detail;
        }
      }
      return REAL_PURCHASE_ORDER_MESSAGE;
    }
    if (response.status === 500) {
      return "Ocurrió un error inesperado al guardar las órdenes de compra.";
    }
    const data = response.data;
    if (Array.isArray(data) && data.length > 0) {
      return String(data[0]);
    }
    if (data && typeof data === "object" && "errors" in data) {
      const errors = (data as { errors: Array<{ detail?: string; title?: string }> })
        .errors;
      const first = errors?.[0];
      if (first?.detail) {
        return first.detail;
      }
      if (first?.title) {
        return first.title;
      }
    }
    return "No se pudo guardar las órdenes de compra asociadas a la simulación.";
  };

  const exportToMonday = async () => {
    if (!simulationId) {
      setError(
        "No hay una simulación activa. Cree o reanude una simulación antes de exportar a Monday.",
      );
      return;
    }
    if (newOrders.length === 0) {
      return;
    }
    setIsExporting(true);
    setError(null);
    try {
      const created = await apiCreateSupplyDocumentsForOrders(
        newOrders,
        simulationId,
        { forMondayExport: true },
      );
      for (const doc of created) {
        await apiProcurementExportSupplyDocument(doc.id);
      }
      await ApiUpdateOrderList(newOrders);
      toast({
        title: "Exportación a Monday encolada",
        description: `Se registraron ${created.length} orden(es) para procesamiento en Monday.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      const message = formatSaveError(err);
      setError(message);
      toast({
        title: "No se pudo exportar a Monday",
        description: message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Carga en Profit las órdenes que todavía no se enviaron.
   *
   * Se materializan primero como `SupplyDocument` y recién después se envían:
   * las OC del simulador viven en sesión, y una sesión vencida a mitad del
   * envío dejaría una orden real en el ERP sin nada acá que la explique.
   *
   * Se envía una por una y se acumulan los resultados en vez de cortar al
   * primer problema: un rechazo no crea nada en Profit, así que frenar sólo
   * escondería el estado de las demás.
   */
  /** Órdenes candidatas al envío, con lo que le falta a cada una. */
  const profitCandidates = newOrders
    .filter((order) =>
      canPushToProfit(profitStatuses[String(order.id ?? "").trim()]?.state),
    )
    .map(checkProfitReadiness);

  const openProfitConfirm = () => {
    if (!simulationId) {
      setError(
        "No hay una simulación activa. Cree o reanude una simulación antes de cargar en Profit.",
      );
      return;
    }
    if (profitCandidates.length === 0) {
      toast({
        title: "No hay órdenes para cargar",
        description: "Todas las órdenes ya se enviaron a Profit.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setIsProfitConfirmOpen(true);
  };

  const pushToProfit = async () => {
    if (!simulationId) {
      return;
    }
    // Sólo las completas. Mandar una orden a la que le falta el proveedor
    // gastaría el request para recibir el mismo rechazo que ya se ve en el
    // modal, después de haber materializado el documento.
    const pushable = profitCandidates
      .filter((candidate) => candidate.issues.length === 0)
      .map((candidate) => candidate.order);
    if (pushable.length === 0) {
      return;
    }

    setIsPushingProfit(true);
    setError(null);
    try {
      const created = await apiCreateSupplyDocumentsForOrders(
        pushable,
        simulationId,
        { forProfitExport: true },
      );
      const statuses: ProfitPushStatus[] = [];
      const failures: string[] = [];
      let sent = 0;
      for (let i = 0; i < created.length; i += 1) {
        const doc = created[i];
        const label = `#${doc.document_number || pushable[i]?.number || doc.id}`;
        try {
          const pushStatus = await apiPushOrderToProfit(simulationId, doc.id);
          statuses.push(pushStatus);
          if (pushStatus.state === "rejected" || pushStatus.state === "failed") {
            failures.push(`${label}: ${pushStatus.message}`);
          } else {
            sent += 1;
          }
        } catch (err) {
          // El mensaje del datamart (o del rechazo local, que tiene la misma
          // forma) va tal cual: es lo único accionable que tiene el usuario.
          failures.push(
            `${label}: ${
              err instanceof ProfitExportError
                ? err.message
                : "No se pudo contactar al ERP."
            }`,
          );
        }
      }
      profitPushes.apply(statuses);
      // Los documentos recién creados traen id numérico; sin propagarlo, el
      // badge no encontraría su orden y el botón la ofrecería de nuevo.
      const idByNumber = new Map(
        created.map((doc, index) => [
          String(pushable[index]?.number ?? doc.document_number),
          doc.id,
        ]),
      );
      const withIds = newOrders.map((order) => {
        const docId = idByNumber.get(String(order.number));
        return docId ? { ...order, id: docId } : order;
      });
      setNewOrders(withIds);
      await ApiUpdateOrderList(withIds);

      if (failures.length > 0) {
        setError(failures.join(" · "));
      }
      toast({
        title: sent > 0 ? "Órdenes enviadas a Profit" : "Ninguna orden se creó",
        description:
          failures.length === 0
            ? `Se enviaron ${sent} orden(es). El estado final puede tardar unos minutos.`
            : failures.join("\n"),
        status: failures.length === 0 ? "success" : "warning",
        duration: failures.length === 0 ? 5000 : 10000,
        isClosable: true,
      });
    } catch (err) {
      const message = formatSaveError(err);
      setError(message);
      toast({
        title: "No se pudo cargar en Profit",
        description: message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsPushingProfit(false);
      setIsProfitConfirmOpen(false);
    }
  };

  /**
   * No se sabe qué órdenes están enviadas: o la consulta de apertura no
   * terminó, o falló. En los dos casos se bloquea, que es la opción
   * conservadora — abrir editable sin poder verificar es cómo se manda dos
   * veces la misma orden a un ERP que no permite anularlas.
   */
  const isProfitStateUnknown =
    Boolean(simulationId) &&
    (profitPushes.isLoading || Boolean(profitPushes.loadError));

  /** Órdenes que todavía se pueden enviar (nunca enviadas o rechazadas). */
  const profitPushableCount = newOrders.filter((order) =>
    canPushToProfit(profitStatuses[String(order.id ?? "").trim()]?.state),
  ).length;

  const patchOrderList = async () => {
    if (!simulationId) {
      setError(
        "No hay una simulación activa. Cree o reanude una simulación antes de guardar órdenes.",
      );
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const ordersToCreate = ordersPendingSupplyDocumentCreate(newOrders);
      if (ordersToCreate.length > 0) {
        await apiCreateSupplyDocumentsForOrders(ordersToCreate, simulationId);
      }
      // Las que ya existen no pasan por create(): sus renglones sólo se
      // sincronizan acá. Sin esto, agregar/editar productos de una OC ya
      // guardada nunca llega al backend, aunque la UI lo muestre.
      const ordersToUpdate = newOrders.filter(isPersistedSupplyDocumentOrder);
      for (const order of ordersToUpdate) {
        await apiUpdateSupplyDocumentLines(String(order.id), order);
      }
      await ApiUpdateOrderList(newOrders);
      reloader(Math.random());
      modalOnClose();
    } catch (err) {
      setError(formatSaveError(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {modalIsOpen ? (
        <Portal>
          <Box
            position="fixed"
            top={0}
            right={0}
            h="100vh"
            w={{ base: "100%", md: "560px" }}
            maxW={{ base: "100%", md: "560px" }}
            zIndex={1400}
            display="flex"
            flexDirection="column"
            bg={drawerSurfaceBg}
            boxShadow="2xl"
          >
            <CloseButton
              position="absolute"
              top={2}
              right={3}
              zIndex={1}
              onClick={modalOnClose}
            />
            <Box
              borderBottomWidth="1px"
              flexShrink={0}
              pe={12}
              px={6}
              py={4}
            >
              <Text fontSize="lg" fontWeight="700" lineHeight="short">
                Nuevas Órdenes de Compra
              </Text>
              <Text
                fontSize="sm"
                fontWeight="normal"
                color={drawerSubtitleColor}
                mt={1}
              >
                Simulá llegadas sin perder de vista el mapa de cobertura
              </Text>
            </Box>
            <Box
              flex="1"
              minH={0}
              overflowY="auto"
              overflowX="auto"
              px={4}
              pb={4}
            >
              {isLoadingSimulationOrders ? (
                <Text py="25px" textAlign="center">
                  Cargando órdenes de compra de la simulación…
                </Text>
              ) : newOrders.length === 0 ? (
                <>
                  <SlideFade in={helperIsOpen} offsetY="20px">
                    <VStack py="25px">
                      <Text>Parece que aún no hay compras nuevas.</Text>
                      <Icon as={FaShip} w="100px" h="125px" />
                      <Button
                        onClick={() => {
                          setOpenHelper(false);
                          addNewOrder();
                        }}
                      >
                        <Icon as={MdAddCircle} />
                        <Text ml="10px">Agregar Orden</Text>
                      </Button>
                    </VStack>
                  </SlideFade>
                </>
              ) : (
                <>
                  {profitPushes.loadError ? (
                    <Flex
                      mb="20px"
                      gap="10px"
                      align="center"
                      justify="space-between"
                    >
                      <Box flex="1">
                        <CustomAlert
                          message={profitPushes.loadError}
                          status="warning"
                          borderRadius="20px"
                        />
                      </Box>
                      <Button size="sm" onClick={() => profitPushes.reload()}>
                        Reintentar
                      </Button>
                    </Flex>
                  ) : null}
                  {patchError ? (
                    <CustomAlert
                      message={patchError}
                      status="error"
                      mb="20px"
                      borderRadius="20px"
                    />
                  ) : (
                    <CustomAlert
                      message="El simulador no recalculará hasta que se presione el botón guardar."
                      status="info"
                      mb="20px"
                      borderRadius="20px"
                    />
                  )}
                  {getNewOrders()}
                </>
              )}
            </Box>

            <Box
              borderTopWidth="1px"
              flexShrink={0}
              gap={3}
              display="flex"
              flexWrap="wrap"
              justifyContent="flex-end"
              px={6}
              py={4}
            >
              {newOrders.length > 0 ? (
                <>
                  <Button
                    colorScheme="purple"
                    onClick={exportToMonday}
                    isLoading={isExporting}
                    loadingText="Enviando"
                    isDisabled={
                      isSaving ||
                      isExporting ||
                      isRemovingOrder ||
                      isPushingProfit ||
                      !simulationId ||
                      newOrders.length === 0 ||
                      // Mismo motivo que en Guardar: mandaría almacén/costo
                      // vacíos si el autocompletado todavía no escribió.
                      hasPendingPurchaseData
                    }
                  >
                    <Icon as={TbBrandMonday} mr="10px" h="3em" w="1.5em" />
                    Agregar a Monday
                  </Button>
                  {/*
                    El `me="auto"` vive en el ÚLTIMO botón del grupo izquierdo.
                    Si se quedara en el de Monday, el margen automático se
                    metería entre los dos y los separaría por todo el ancho del
                    drawer — justo lo contrario de dejarlos juntos.
                  */}
                  <Tooltip
                    label={
                      hasPendingPurchaseData
                        ? "Esperando el almacén/costo del maestro para los productos recién agregados."
                        : profitPushes.loadError
                          ? "No se pudo verificar qué órdenes ya se enviaron."
                          : profitPushableCount === 0
                            ? "Todas las órdenes ya se enviaron a Profit."
                            : "Crea la orden en el ERP. No se puede anular desde acá."
                    }
                  >
                    <Button
                      colorScheme="teal"
                      onClick={openProfitConfirm}
                      me="auto"
                      isLoading={isPushingProfit}
                      loadingText="Cargando"
                      isDisabled={
                        isSaving ||
                        isExporting ||
                        isRemovingOrder ||
                        isPushingProfit ||
                        !simulationId ||
                        // Sin saber qué se envió, enviar es apostar a duplicar.
                        isProfitStateUnknown ||
                        profitPushableCount === 0 ||
                        hasPendingPurchaseData
                      }
                    >
                      <Icon
                        as={TbBuildingWarehouse}
                        mr="10px"
                        h="3em"
                        w="1.5em"
                      />
                      Cargar en Profit
                    </Button>
                  </Tooltip>
                </>
              ) : null}
              <Tooltip
                label={
                  hasPendingPurchaseData
                    ? "Esperando el almacén/costo del maestro para los productos recién agregados."
                    : undefined
                }
                isDisabled={!hasPendingPurchaseData}
              >
                <Button
                  colorScheme="blue"
                  isLoading={isSaving}
                  loadingText="Guardando"
                  // `hasPendingPurchaseData`: si se guarda mientras el GET de
                  // purchase-defaults sigue en vuelo, el payload sale con
                  // `unit_cost`/`warehouse_code` vacíos aunque un instante
                  // después se vean llenos en pantalla — es la condición de
                  // carrera que dejaba renglones incompletos en la base.
                  isDisabled={isSaving || isRemovingOrder || hasPendingPurchaseData}
                  onClick={() => {
                    patchOrderList();
                  }}
                >
                  <Icon as={MdSave} mr="10px" h="3em" w="1.5em" />
                  Guardar
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Portal>
      ) : null}
      <ProfitPushConfirmModal
        isOpen={isProfitConfirmOpen}
        onClose={() => setIsProfitConfirmOpen(false)}
        onConfirm={pushToProfit}
        isSubmitting={isPushingProfit}
        candidates={profitCandidates}
      />
    </>
  );
};
